import * as v0 from "@aidkitorg/types/lib/survey";
import { useContext, useMemo, useEffect } from "react";
import InterfaceContext from "../Context";
import { useSurveySections } from "../Contexts/SurveySectionsContext";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { GoBackToSectionButton, languageContent, safeParse } from "../Util";
import { DistroQuestionProps, InfoDict } from "./Props";
import type { Question, Sections } from "../Apply";
import { AssistanceAnalysisContent, Attachment, AttachmentType, getInlineAlertContent } from "./Attachment";
import { AsyncMediaContext, AsyncMediaContextType } from "../Contexts/AsyncMediaContext";
import { Alert, Button, Card, CardBody, CardHeader, Icon } from "@aidkitorg/component-library";
import { useLocalizedStrings } from "../Localization";

type InlineAlert = NonNullable<NonNullable<v0.Attachment["assistance"]>["inlineAlerts"]>[number]

type AttachmentQuestion = Question & { "Field Type": "Attachment" } & { config?: v0.Attachment, section?: NonNullable<Sections["sections"]>[number] }

type ImageUrlFieldMap<T> = { [imageUrl: string]: { [field: string]: T } }
type JobsByImageUrl = ImageUrlFieldMap<{ jobId: string, field: string } >
type AnalysesByImageUrl = ImageUrlFieldMap<string>
type ErrorsByImageUrl = ImageUrlFieldMap<string>
type Section = NonNullable<Sections["sections"]>[number];

function imageJobIsPending({ imageUrl, jobs, analyses }: {
  imageUrl: string, 
  jobs: JobsByImageUrl,
  analyses: AnalysesByImageUrl
}) {
  return Object.entries(jobs[imageUrl] ?? {})
    .some(([field, { jobId }]) => jobId && !analyses[imageUrl]?.[field]);
}

export default function DocumentSummary({
  component,
  info,
  setInfo,  
  uid,
  questionProps
}: DistroQuestionProps<v0.DocumentSummary>) {
  const L = useLocalizedStrings();
  const { sections } = useSurveySections();
  const { analysisTargetFieldsGrouped, state: asyncMediaState } = useContext(AsyncMediaContext);
  const context = useContext(InterfaceContext);
  const { groupBy } = component;
  
  const descriptionContent = component.descriptionContent[context.lang] ?? component.descriptionContent["en"] ?? "";
  const descriptionMarkdown = descriptionContent.trim() ? useModularMarkdown({
    info,
    content: descriptionContent
  }) : null;
  const noAlertsContent = component.noAlertsContent?.[context.lang] ?? component.noAlertsContent?.["en"] ?? "";
  const noAlertsMarkdown = noAlertsContent.trim() ? useModularMarkdown({
    info,
    content: noAlertsContent
  }) : null;
  
  const matchingAttachmentQuestions = useMemo(
    () => {  
      const questionsSectionMap = (sections?.sections ?? []).reduce(
        (acc, section) => { 
          section.Questions?.filter(q => q["Field Type"] === "Attachment").forEach(q => {
            acc.set(q, section);
          });
          return acc;
        },
        new Map<Question, Section>()
      ); 
      
      return (sections?.sections?.flatMap(section => section.Questions) ?? [])
        .filter(q => q["Field Type"] === "Attachment")
        .filter(
          q => !component?.questionFields?.length || component.questionFields.includes(q["Target Field"]!)
        )
        .map(q => ({ 
          ...(q as AttachmentQuestion), 
          config: {
            targetField: q["Target Field"],
            ...safeParse(q.Metadata!, undefined),
          } as v0.Attachment, 
          section: questionsSectionMap.get(q)
        }))
    },
    [sections, component.questionFields]
  );
  
  const matchingAttachmentQuestionsByTargetField = matchingAttachmentQuestions.reduce(
    (acc, v) => { 
      if (v.config.targetField) {
        acc[v.config.targetField] = v;
      }
      return acc;
    },
    {} as { [targetField: string]: typeof matchingAttachmentQuestions[number]}
  )
  
  const targetFieldDeps = analysisTargetFieldsGrouped
    .filter(({ targetField }) => !!matchingAttachmentQuestionsByTargetField[targetField])
    .flatMap(({ targetField, jobTargetField }) => [
      info[targetField],
      ...(jobTargetField ? [info[jobTargetField]] : [])
    ].concat(
      matchingAttachmentQuestions.map(q => info[q["Target Field"]!]))
    );  
  
  // compute which attachment questions have inline alerts to show (or a loading indicator)
  const { questionsWithAlertContent, pendingJobsInProgress } = useMemo(
    () => {
      let pendingJobsInProgress = false;
      const questionsWithAlertContent = matchingAttachmentQuestions.filter(
        q => {
          const targetField = q["Target Field"];
          if (!targetField || !q.config.assistance || q.config.assistance.disabled) {
            return false;
          }
          const fieldValue = info[targetField] ?? "";
          // Non-image URLs will be filtered out by the check on inlineAlerts towards the end of this function.
          // We can't simply filter on extensions because we need to support directories as well.
          const imageUrls = fieldValue.split(',');
                    
          // create a map of all analysis and job target fields
          // keyed by imageUrl
          const { analyses, jobs, errors } = analysisTargetFieldsGrouped.reduce(
            (acc, v) => {
              const analysesByUrl = safeParse(info[v.targetField]!, null);
              const errorField = `${v.targetField}_error`;
              const errorsByUrl = safeParse(info[errorField]!, null);
              for (const imageUrl of imageUrls) {
                if (analysesByUrl?.[imageUrl]) {
                  acc.analyses ??= {};
                  acc.analyses[imageUrl] ??= {};
                  acc.analyses[imageUrl][v.targetField] = analysesByUrl[imageUrl];
                }
                
                if (errorsByUrl?.[imageUrl]) {
                  acc.errors ??= {};
                  acc.errors[imageUrl] ??= {};
                  acc.errors[imageUrl][errorField] = errorsByUrl[imageUrl];
                }

                if (v.jobTargetField) {
                  const jobsByUrl = safeParse(info[v.jobTargetField]!, null);
                  if (jobsByUrl?.[imageUrl]) {
                    acc.jobs ??= {};
                    acc.jobs[imageUrl] ??= {};
                    acc.jobs[imageUrl][v.targetField] = typeof jobsByUrl[imageUrl] === "string" ? { jobId: jobsByUrl[imageUrl], field: v.targetField } : jobsByUrl[imageUrl];
                  }
                }
              }
              return acc;
            },
            {
              analyses: {},
              jobs: {},
              errors: {}
            } as {
              analyses: AnalysesByImageUrl,
              jobs: JobsByImageUrl,
              errors: ErrorsByImageUrl
            }
          );
          
          
          if (!pendingJobsInProgress) { 
            pendingJobsInProgress = imageUrls.some(imageUrl => imageJobIsPending({ imageUrl, analyses, jobs }));
          }
          
          // try to find a document that either has alert content, or still has a pending job running
          return imageUrls.some(
            // for each image url, grab the analysis results from info 
            // and determine if there is any alert content
            imageUrl => (q.config.assistance?.inlineAlerts ?? []).some(
              inlineAlert => getInlineAlertContent({
                lang: context.lang,
                content: inlineAlert.content,
                info: {
                  ...info,
                  ...(analyses[imageUrl] ?? {})
                },
                uid
              })
                // check if there is a pending job running and there's no error
                || (
                  imageJobIsPending({ imageUrl, analyses, jobs }) && !Object.values(errors[imageUrl] ?? {}).length
                )
            )
          );
        }
      );
      
      return {
        questionsWithAlertContent,
        pendingJobsInProgress
      }
    },
    [analysisTargetFieldsGrouped, matchingAttachmentQuestions, ...targetFieldDeps]
  );
  
  const sectionsWithAlertContent = useMemo(
    () => { 
      return questionsWithAlertContent.reduce(
        (acc, q) => {
          if (q.section) {
            acc.set(q.section, [
              ...(acc.get(q.section) ?? []),
              q
            ]);
          }
          return acc;
        },
        new Map<Section, typeof questionsWithAlertContent>()
      );
    },
    [questionsWithAlertContent, groupBy]
  )
  
  // set main target field
  useEffect(
    () => {
      if (!component.targetField) return;
      if (!info[component.targetField] && (component.advisory || !pendingJobsInProgress)) { 
        questionProps.setInfoKey(
          component.targetField,
          new Date().toISOString(),
          true,
          false
        );        
      }
      else if (!!info[component.targetField] && (!component.advisory && pendingJobsInProgress)) {
        questionProps.setInfoKey(
          component.targetField,
          "",
          true,
          false
        );
      }
    },
    [questionProps.setInfoKey, component.targetField, component.advisory, pendingJobsInProgress, info[component.targetField]]
  );
  
  useEffect(() => {
    if (questionProps.id) {
      const questionElement = document.querySelector<HTMLDivElement>(`[data-name="${questionProps.id}"]`);
      if (questionElement) { 
        questionElement.style.display = !questionsWithAlertContent.length && !noAlertsMarkdown ? "none" : "block";
      }
    }
  }, [questionProps.id, questionsWithAlertContent.length, noAlertsMarkdown]);
  
  // if (!matchingAttachmentQuestions.length) {
  //   return <Alert
  //     iconName="QuestionMarkCircleIcon"
  //     variant="secondary">{L.ai_coach.document_signals.no_documents_uploaded}</Alert>;    
  // }

  if (!questionsWithAlertContent.length) {    
    return <Alert
      iconName="CheckCircleIcon"
      variant="success">{ noAlertsMarkdown ?? L.ai_coach.document_signals.no_issues_with_your_documents }</Alert>;    
  }  
  
  function getMainContent() { 
    switch (groupBy) {
      case "section":        
        return Array.from(sectionsWithAlertContent.entries())
          .map(([section, questions], i) => <SectionSummaryItem key={`section_summary_item_${i}`} section={section} questions={questions} info={info} hideHeadings={component.hideHeadings} /> )
      case "question":
      default:
        return questionsWithAlertContent
          .map((q, i) => {
            return <Card key={`document_summary_question_${i}`}>
              <QuestionSummaryItem
                hideTitle={component.hideHeadings}
                info={info}
                uid={uid}
                question={q} />
            </Card>
          })
    }    
  }  
  
  return <>
    { !pendingJobsInProgress && descriptionMarkdown }    
    <div className="flex flex-col gap-8">
      {getMainContent()}
    </div>
  </>
}

function SectionSummaryItem({ section, questions, info, uid, hideHeadings }: {
  section: Section,
  questions: AttachmentQuestion[],
  info: InfoDict
  uid?: string,
  hideHeadings?: boolean
}) {
  const context = useContext(InterfaceContext);  
  const content: string = (section as Record<string, any>)[languageContent(context.lang)];    
  const marked = useModularMarkdown({
    info,
    content
  });
  
  return <div>
    {!hideHeadings && <div className="text-lg break-all">{marked}</div>}
    <div className="flex flex-col gap-12">
      { questions.map((q, i) => <QuestionSummaryItem 
        key={`section_question_${i}`}
        hideTitle={true}
        question={q}
        info={info}
        uid={uid} />) }        
    </div>
  </div>
}

function QuestionSummaryItem({
  question,
  info,
  uid,
  hideTitle
}: {
  question: AttachmentQuestion,
  info: InfoDict,
  uid?: string,
  hideTitle?: boolean
}) {   
  const { targetField } = question.config!;
  const context = useContext(InterfaceContext);  
  const content: string = (question as Record<string, any>)[languageContent(context.lang)];    
  const marked = useModularMarkdown({
    info,
    content
  });
    
  if (!targetField || !info[targetField]) {
    return <></>;
  }
  
  const imageUrls = (info[targetField])!.split(',');
    
  const documentItems = imageUrls.map(
    imageUrl => <DocumentSummaryItem
      key={imageUrl}
      imageUrl={imageUrl}
      info={info}
      question={question}
      targetField={targetField}
      uid={uid}
    />
  );
          
  return <>
    {!hideTitle && <div>{marked}</div>}
    {!!imageUrls.length && <div className="flex flex-col gap-8">
      { documentItems }
    </div> }    
  </>
}

export function DocumentSummaryItem({ 
  imageUrl,
  info,
  targetField,
  question,
  uid,
}: { 
  imageUrl: string, 
  info: InfoDict,
  targetField: string,
  question: AttachmentQuestion
  uid?: string,
}) {
  const L = useLocalizedStrings();
  const { state: asyncMediaState } = useContext(AsyncMediaContext);
  const context = useContext(InterfaceContext);
  const { analysisTargetFieldsGrouped } = useContext(AsyncMediaContext);
    
  const targetFieldDeps = analysisTargetFieldsGrouped.flatMap(({ targetField, jobTargetField }) => [
    info[targetField],
    ...(jobTargetField ? [info[jobTargetField]] : [])
  ]);

  // get analysis target fields and values
  const { analyses, jobs, errors } = useMemo(
    () => analysisTargetFieldsGrouped.reduce(
      (acc, v) => {
        const errorField = `${v.targetField}_error`;
        const analysesByUrl = safeParse(info[v.targetField]!, null);
        if (analysesByUrl?.[imageUrl]) {
          acc.analyses ??= {};
          acc.analyses[v.targetField] = analysesByUrl[imageUrl]!;
        }
        const errorsByUrl = safeParse(info[errorField]!, null);
        if (errorsByUrl?.[imageUrl]) {
          acc.errors ??= {};
          acc.errors[errorField] = errorsByUrl[imageUrl]!;
        }        
        if (v.jobTargetField) {
          const jobsByUrl = safeParse(info[v.jobTargetField]!, null);
          if (jobsByUrl?.[imageUrl]) {
            acc.jobs ??= {};
            acc.jobs[v.targetField] = typeof jobsByUrl[imageUrl] === "string" ? { jobId: jobsByUrl[imageUrl], field: v.targetField } : jobsByUrl[imageUrl];
          }
        }
        return acc;
      },
      {
        analyses: {},
        jobs: {},
        errors: {}
      } as {
        analyses: AnalysesByImageUrl[keyof AnalysesByImageUrl],
        jobs: JobsByImageUrl[keyof JobsByImageUrl],
        errors: ErrorsByImageUrl[keyof ErrorsByImageUrl]
      }
    ),
    [...targetFieldDeps]
  );

  const assistance = question.config?.assistance;
  
  const activeInlineAlerts: NonNullable<v0.Attachment["assistance"]>["inlineAlerts"] = [];
  for (const inlineAlert of assistance?.inlineAlerts ?? []) {
    if (
      getInlineAlertContent({
        lang: context.lang,
        content: inlineAlert.content,
        info: { ...info, ...analyses },
        uid
      }) || (imageJobIsPending({
        imageUrl,
        jobs: { [imageUrl]: jobs! },
        analyses: { [imageUrl]: analyses! }
      }) && !(Object.values(errors).length))
    ) { 
      activeInlineAlerts.push(inlineAlert);
    }
  }
  
  
  if (!activeInlineAlerts.length) {
    return <></>;    
  }
        
  const alertOrLoadingContent = <AssistanceAnalysisContent
    assistance={assistance}
    info={info}
    analyses={analyses}
    jobs={jobs}
    uid={uid}
  />;  
    
  return <div className="flex flex-col gap-2">
    <Attachment
      style={{listStyle: "none"}}
      url={imageUrl}
      key={imageUrl} />      
    {question.section?.id ? <div className="flex items-center justify-center">
      <GoBackToSectionButton sectionId={question.section?.id} />
    </div> : undefined}
    {alertOrLoadingContent}
  </div>
}
